import type { ReactNode } from "react"
import { useCallback, useEffect, useId, useMemo, useState } from "react"
import { Eye, Plus, Save, Trash2 } from "lucide-react"
import toast from "react-hot-toast"
import { useHotkeys } from "react-hotkeys-hook"

import { seoPageSchema } from "@aix/apiutils/src/seo/seoPage.schema"
import type {
  SeoMediaAsset,
  SeoMediaRole,
  SeoPage,
  SeoPageExample,
  SeoPageSection,
} from "@aix/apiutils/src/seo/seoPage.schema"
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@aix/ui"

import { trpc } from "../lib/trpc"

interface SEOPageEditorProps {
  productKey: string
  contentHash: string
  initialContent: SeoPage
  onCancel: () => void
  onDirtyChange: (dirty: boolean) => void
  onSaved: () => void
}

export default function SEOPageEditor({
  productKey,
  contentHash,
  initialContent,
  onCancel,
  onDirtyChange,
  onSaved,
}: SEOPageEditorProps) {
  const [draft, setDraft] = useState<SeoPage>(initialContent)
  const [note, setNote] = useState("Edited in AIX Command Center")
  const [validationMessages, setValidationMessages] = useState<string[]>([])
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)

  const originalJson = useMemo(() => JSON.stringify(initialContent), [initialContent])
  const draftJson = useMemo(() => JSON.stringify(draft), [draft])
  const dirty = originalJson !== draftJson

  useEffect(() => {
    setDraft(initialContent)
    setPreviewHtml(null)
    setValidationMessages([])
  }, [contentHash, initialContent])

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange])
  useEffect(() => () => onDirtyChange(false), [onDirtyChange])

  const previewMutation = trpc.seo.pages.previewContent.useMutation({
    onError: (error) => toast.error(error.message || "Preview failed"),
  })
  const sources = trpc.seo.sources.list.useQuery({ productKey, limit: 50 })
  const saveMutation = trpc.seo.pages.upsert.useMutation({
    onError: (error) => toast.error(error.message || "Save failed"),
    onSuccess: () => {
      toast.success("Page revision saved as a draft")
      onDirtyChange(false)
      onSaved()
    },
  })

  const validate = useCallback((): SeoPage | null => {
    const parsed = seoPageSchema.safeParse(draft)
    if (parsed.success) {
      setValidationMessages([])
      return parsed.data
    }
    setValidationMessages(parsed.error.issues.map((issue) => `${issue.path.join(".") || "Page"}: ${issue.message}`))
    return null
  }, [draft])

  const save = useCallback(() => {
    const content = validate()
    if (!content) return
    if (!note.trim()) {
      setValidationMessages(["Change note: describe why this revision is being saved."])
      return
    }
    saveMutation.mutate({
      productKey,
      slug: content.slug,
      content,
      note: note.trim(),
      expectedContentHash: contentHash,
    })
  }, [contentHash, note, productKey, saveMutation, validate])

  useHotkeys("meta+s, ctrl+s", save, { preventDefault: true, enableOnFormTags: true }, [save])

  const previewUnsaved = async () => {
    const content = validate()
    if (!content) return
    const rendered = await previewMutation.mutateAsync({ productKey, slug: content.slug, content })
    setPreviewHtml(rendered.html)
  }

  const patchDraft = (patch: Partial<SeoPage>) => setDraft((current) => ({ ...current, ...patch }))

  return (
    <div className="space-y-5 pb-8">
      <header className="sticky top-0 z-10 -mx-4 flex flex-wrap items-center gap-2 border-b border-border bg-background px-4 py-3">
        <div className="mr-auto">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold">Edit page</h2>
            {dirty ? (
              <Badge variant="outline" className="border-amber-500 text-amber-700">
                Unsaved
              </Badge>
            ) : (
              <Badge variant="secondary">Saved</Badge>
            )}
          </div>
          <p className="font-mono text-[11px] text-muted-foreground">{draft.slug}</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void previewUnsaved()}
          loading={previewMutation.isLoading}
        >
          <Eye className="mr-1.5 h-4 w-4" /> Preview unsaved
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={save} loading={saveMutation.isLoading} disabled={!dirty}>
          <Save className="mr-1.5 h-4 w-4" /> Save revision
        </Button>
      </header>

      {validationMessages.length ? (
        <section className="rounded-md border border-destructive bg-destructive/10 p-3">
          <p className="text-sm font-semibold text-destructive">Fix these fields before saving</p>
          <ul className="mt-1 list-disc pl-5 text-xs text-destructive">
            {validationMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {previewHtml ? (
        <section className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold">Unsaved preview</h3>
              <p className="text-xs text-muted-foreground">Rendered from the form below. Nothing has been persisted.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setPreviewHtml(null)}>
              Close preview
            </Button>
          </div>
          <iframe
            title={`Unsaved preview for ${draft.slug}`}
            srcDoc={previewHtml}
            sandbox="allow-popups"
            className="h-[520px] w-full rounded-md border border-border bg-white"
          />
        </section>
      ) : null}

      <AgentProposalReview
        productKey={productKey}
        slug={draft.slug}
        expectedContentHash={contentHash}
        onApply={(proposedContent) => {
          setDraft(proposedContent)
          setPreviewHtml(null)
          toast.success("Agent proposal loaded into the editor; review and save it explicitly")
        }}
      />

      <EditorSection
        title="Search result and page promise"
        description="What a searcher sees and what the page promises to deliver."
      >
        <EditorInput label="Meta title" value={draft.metaTitle} onChange={(metaTitle) => patchDraft({ metaTitle })} />
        <EditorTextarea
          label="Meta description"
          value={draft.metaDescription}
          onChange={(metaDescription) => patchDraft({ metaDescription })}
        />
        <EditorInput label="Page headline" value={draft.h1} onChange={(h1) => patchDraft({ h1 })} />
        <EditorTextarea
          label="One-sentence promise"
          value={draft.promise}
          onChange={(promise) => patchDraft({ promise })}
        />
        <EditorInput label="Primary keyword" value={draft.keyword} onChange={(keyword) => patchDraft({ keyword })} />
      </EditorSection>

      <EditorSection
        title="Main content"
        description="Use one paragraph or idea per row so the renderer can compose it consistently."
      >
        <StringListEditor
          label="Body paragraphs"
          values={draft.body ?? []}
          multiline
          onChange={(body) => patchDraft({ body })}
        />
        <StringListEditor
          label="Use cases"
          values={draft.useCases ?? []}
          onChange={(useCases) => patchDraft({ useCases })}
        />
      </EditorSection>

      <EditorSection title="Sections" description="Reusable headings and paragraphs for deeper educational content.">
        <SectionsEditor values={draft.sections ?? []} onChange={(sections) => patchDraft({ sections })} />
      </EditorSection>

      <EditorSection
        title="Examples"
        description="Show a concrete prompt and result. Existing attached media stays with each example."
      >
        <ExamplesEditor values={draft.examples ?? []} onChange={(examples) => patchDraft({ examples })} />
      </EditorSection>

      <EditorSection
        title="Frequently asked questions"
        description="Answer real objections and follow-up questions in plain language."
      >
        <FaqEditor values={draft.faq} onChange={(faq) => patchDraft({ faq })} />
      </EditorSection>

      <EditorSection
        title="Internal links and action"
        description="Guide readers to relevant pages and a clear product action."
      >
        <StringListEditor
          label="Internal page paths"
          values={draft.internalLinks}
          onChange={(internalLinks) => patchDraft({ internalLinks })}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <EditorInput
            label="Button label"
            value={draft.cta?.label ?? ""}
            onChange={(label) =>
              patchDraft({ cta: { label, href: draft.cta?.href ?? "", supportingText: draft.cta?.supportingText } })
            }
          />
          <EditorInput
            label="Button destination"
            value={draft.cta?.href ?? ""}
            onChange={(href) =>
              patchDraft({ cta: { label: draft.cta?.label ?? "", href, supportingText: draft.cta?.supportingText } })
            }
          />
        </div>
        <EditorInput
          label="Supporting text"
          value={draft.cta?.supportingText ?? ""}
          onChange={(supportingText) =>
            patchDraft({ cta: { label: draft.cta?.label ?? "", href: draft.cta?.href ?? "", supportingText } })
          }
        />
      </EditorSection>

      <EditorSection
        title="Media and evidence"
        description="Asset picking and evidence repair use the same page record; their guided controls are the next operator layer."
      >
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline">{draft.heroImage ? "Hero attached" : "No hero"}</Badge>
          <Badge variant="outline">{draft.media?.length ?? 0} media assets</Badge>
          <Badge variant="outline">{draft.evidence?.length ?? 0} evidence records</Badge>
        </div>
        <MediaEditor
          heroAssetId={draft.heroImage?.assetId}
          values={draft.media ?? []}
          onChange={(media) => patchDraft({ media })}
          onSetHero={(asset) => {
            if (asset.type !== "image") return
            patchDraft({
              heroImage: {
                ...asset,
                type: "image",
              },
            })
          }}
        />
        <div className="space-y-2">
          <Label>Available evidence</Label>
          {sources.isLoading ? <p className="text-xs text-muted-foreground">Loading evidence sources…</p> : null}
          {sources.error ? (
            <p className="text-xs text-destructive">Evidence sources unavailable: {sources.error.message}</p>
          ) : null}
          {sources.data?.rows.map((source) => {
            const attached = (draft.evidence ?? []).some((evidence) => evidence.id === source.evidence.id)
            return (
              <div
                key={source.id}
                className="flex flex-wrap items-center gap-2 rounded border border-border p-2 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {source.evidence.title ?? source.evidence.sourceUrl ?? source.sourceType}
                  </p>
                  <p className="text-muted-foreground">
                    Rights: {source.rightsStatus} · {source.publishable ? "publishable" : "research only"}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!source.publishable || attached}
                  title={!source.publishable ? "Rights review has not made this source publishable" : undefined}
                  onClick={() => patchDraft({ evidence: [...(draft.evidence ?? []), source.evidence] })}
                >
                  {attached ? "Attached" : "Attach"}
                </Button>
              </div>
            )
          })}
          {(draft.evidence ?? []).map((evidence) => (
            <div key={evidence.id} className="flex items-center gap-2 rounded bg-muted p-2 text-xs">
              <span className="min-w-0 flex-1 truncate">
                Attached: {evidence.title ?? evidence.sourceUrl ?? evidence.id}
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() =>
                  patchDraft({ evidence: (draft.evidence ?? []).filter((item) => item.id !== evidence.id) })
                }
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      </EditorSection>

      <EditorSection
        title="Change note"
        description="Every save creates an append-only revision. Describe the intent so another operator can understand it."
      >
        <EditorInput label="Why are you changing this page?" value={note} onChange={setNote} />
        <p className="text-[11px] text-muted-foreground">Shortcut: Cmd/Ctrl + S</p>
      </EditorSection>
    </div>
  )
}

function AgentProposalReview({
  productKey,
  slug,
  expectedContentHash,
  onApply,
}: {
  productKey: string
  slug: string
  expectedContentHash: string
  onApply: (content: SeoPage) => void
}) {
  const [proposalText, setProposalText] = useState("")
  const [proposedContent, setProposedContent] = useState<SeoPage | null>(null)
  const [explanation, setExplanation] = useState<string | null>(null)
  const [evidenceIds, setEvidenceIds] = useState<string[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const proposal = trpc.seo.pages.proposalPreview.useMutation({
    onError: (error) => toast.error(error.message || "Agent proposal validation failed"),
  })

  const reviewProposal = async () => {
    setParseError(null)
    let parsedJson: unknown
    try {
      parsedJson = JSON.parse(proposalText) as unknown
    } catch (error) {
      setParseError(error instanceof Error ? error.message : "Proposal is not valid JSON")
      return
    }
    const wrapper =
      parsedJson && typeof parsedJson === "object" && !Array.isArray(parsedJson)
        ? (parsedJson as Record<string, unknown>)
        : null
    const candidate = wrapper?.proposedContent ?? parsedJson
    const parsedContent = seoPageSchema.safeParse(candidate)
    if (!parsedContent.success) {
      setParseError(parsedContent.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`).join("; "))
      return
    }
    const nextExplanation = typeof wrapper?.explanation === "string" ? wrapper.explanation : null
    const nextEvidenceIds = Array.isArray(wrapper?.evidenceIds)
      ? wrapper.evidenceIds.filter((value): value is string => typeof value === "string")
      : []
    setProposedContent(parsedContent.data)
    setExplanation(nextExplanation)
    setEvidenceIds(nextEvidenceIds)
    await proposal.mutateAsync({
      productKey,
      slug,
      expectedContentHash,
      proposedContent: parsedContent.data,
    })
  }

  return (
    <details className="rounded-md border border-border p-4">
      <summary className="cursor-pointer text-sm font-semibold">Review an agent-proposed repair</summary>
      <div className="mt-3 space-y-3">
        <p className="text-xs text-muted-foreground">
          Paste the structured proposal returned by an SEO agent. Review validates, hashes, diffs, gates, and renders it
          without saving. Apply only loads it into this editor.
        </p>
        <Textarea
          aria-label="Agent repair proposal JSON"
          rows={8}
          value={proposalText}
          onChange={(event) => setProposalText(event.target.value)}
          placeholder='{"explanation":"…","evidenceIds":[],"proposedContent":{"slug":"…"}}'
          className="font-mono text-xs"
        />
        {parseError ? <p className="text-xs text-destructive">{parseError}</p> : null}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void reviewProposal()}
          loading={proposal.isLoading}
          disabled={!proposalText.trim()}
        >
          Validate proposal without saving
        </Button>
        {proposal.data && proposedContent ? (
          <section className="space-y-3 rounded bg-muted p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={proposal.data.persisted ? "destructive" : "success"}>
                {proposal.data.persisted ? "Unexpected write" : "No write performed"}
              </Badge>
              <Badge variant={proposal.data.gateResult?.passed ? "success" : "destructive"}>
                {proposal.data.gateResult?.passed ? "Gate preview passes" : "Gate preview fails"}
              </Badge>
              <Badge variant="outline">{proposal.data.fieldDiff.length} changed field(s)</Badge>
            </div>
            {explanation ? (
              <p className="text-sm">Agent rationale: {explanation}</p>
            ) : (
              <p className="text-xs text-muted-foreground">The proposal did not include a rationale.</p>
            )}
            {evidenceIds.length ? (
              <p className="text-xs text-muted-foreground">Evidence: {evidenceIds.join(", ")}</p>
            ) : null}
            <div className="flex flex-wrap gap-1">
              {proposal.data.fieldDiff.map((difference) => (
                <Badge key={difference.field} variant="secondary" className="font-mono">
                  {difference.field}
                </Badge>
              ))}
            </div>
            {proposal.data.gateResult?.issues.length ? (
              <ul className="list-disc pl-5 text-xs text-destructive">
                {proposal.data.gateResult.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            ) : null}
            <iframe
              title={`Agent proposal preview for ${slug}`}
              srcDoc={proposal.data.rendered.html}
              sandbox="allow-popups"
              className="h-[480px] w-full rounded border border-border bg-white"
            />
            <Button type="button" size="sm" onClick={() => onApply(proposedContent)}>
              Apply proposal to editor
            </Button>
            <p className="text-[11px] text-muted-foreground">
              Applying does not save, gate, approve, or publish. The normal Save revision action remains required.
            </p>
          </section>
        ) : null}
      </div>
    </details>
  )
}

const MEDIA_ROLES: SeoMediaRole[] = ["hero", "inline", "example", "comparison", "social-share", "before", "after"]

function MediaEditor({
  values,
  heroAssetId,
  onChange,
  onSetHero,
}: {
  values: SeoMediaAsset[]
  heroAssetId: string | undefined
  onChange: (values: SeoMediaAsset[]) => void
  onSetHero: (asset: SeoMediaAsset) => void
}) {
  const update = (index: number, patch: Partial<SeoMediaAsset>) => {
    onChange(values.map((asset, assetIndex) => (assetIndex === index ? { ...asset, ...patch } : asset)))
  }
  return (
    <div className="space-y-2">
      <div>
        <Label>Imported media candidates</Label>
        <p className="text-[11px] text-muted-foreground">
          Generation and import workflows add candidates to this page. Selecting a role never changes an asset&apos;s
          rights or provenance.
        </p>
      </div>
      {values.length === 0 ? (
        <p className="rounded bg-muted p-3 text-xs text-muted-foreground">
          No media candidates are attached to this page record yet. Run the bounded media pipeline, then reload this
          editor.
        </p>
      ) : null}
      {values.map((asset, index) => {
        const rightsPublishable =
          asset.rights?.status === "owned" ||
          asset.rights?.status === "licensed" ||
          asset.rights?.status === "public-domain"
        const isHero = Boolean(asset.assetId && asset.assetId === heroAssetId)
        return (
          <div
            key={asset.assetId ?? `${asset.src}-${index}`}
            className="space-y-2 rounded border border-border p-3 text-xs"
          >
            <div className="flex flex-wrap items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{asset.alt || asset.assetId || asset.src}</p>
                <p className="truncate font-mono text-[10px] text-muted-foreground">{asset.src}</p>
              </div>
              <Badge variant={rightsPublishable ? "success" : "destructive"}>
                Rights: {asset.rights?.status ?? "missing"}
              </Badge>
              <Badge variant={asset.provenance ? "success" : "outline"}>
                {asset.provenance ? "Provenance attached" : "No provenance"}
              </Badge>
            </div>
            <div className="grid gap-2 sm:grid-cols-[1fr_180px]">
              <Input
                value={asset.alt}
                aria-label={`Alt text for media ${index + 1}`}
                onChange={(event) => update(index, { alt: event.target.value })}
              />
              <Select
                value={asset.role ?? "inline"}
                onValueChange={(role) => update(index, { role: role as SeoMediaRole })}
              >
                <SelectTrigger aria-label={`Media role for asset ${index + 1}`}>
                  <SelectValue placeholder="Media role" />
                </SelectTrigger>
                <SelectContent>
                  {MEDIA_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2">
              {asset.type === "image" ? (
                <Button type="button" size="sm" variant="outline" disabled={isHero} onClick={() => onSetHero(asset)}>
                  {isHero ? "Current hero" : "Use as hero"}
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Remove media asset ${index + 1} from page`}
                onClick={() => onChange(values.filter((_, assetIndex) => assetIndex !== index))}
              >
                Remove from page
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function EditorSection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-md border border-border p-4">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  )
}

function EditorInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  const id = useId()
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  )
}

function EditorTextarea({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Textarea id={id} value={value} onChange={(event) => onChange(event.target.value)} rows={3} />
    </div>
  )
}

function StringListEditor({
  label,
  values,
  onChange,
  multiline = false,
}: {
  label: string
  values: string[]
  onChange: (values: string[]) => void
  multiline?: boolean
}) {
  const update = (index: number, value: string) =>
    onChange(values.map((item, itemIndex) => (itemIndex === index ? value : item)))
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {values.map((value, index) => (
        <div key={`${label}-${index}`} className="flex items-start gap-2">
          {multiline ? (
            <Textarea
              aria-label={`${label} row ${index + 1}`}
              value={value}
              onChange={(event) => update(index, event.target.value)}
              rows={3}
            />
          ) : (
            <Input
              aria-label={`${label} row ${index + 1}`}
              value={value}
              onChange={(event) => update(index, event.target.value)}
            />
          )}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={`Remove ${label} row ${index + 1}`}
            onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...values, ""])}>
        <Plus className="mr-1.5 h-4 w-4" /> Add
      </Button>
    </div>
  )
}

function FaqEditor({ values, onChange }: { values: SeoPage["faq"]; onChange: (values: SeoPage["faq"]) => void }) {
  const update = (index: number, patch: Partial<SeoPage["faq"][number]>) =>
    onChange(values.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)))
  return (
    <div className="space-y-3">
      {values.map((item, index) => (
        <div key={`faq-${index}`} className="space-y-2 rounded border border-border p-3">
          <div className="flex items-center justify-between">
            <Label>Question {index + 1}</Label>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={`Remove question ${index + 1}`}
              onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Input
            aria-label={`Question ${index + 1}`}
            value={item.q}
            onChange={(event) => update(index, { q: event.target.value })}
          />
          <Textarea
            aria-label={`Answer ${index + 1}`}
            value={item.a}
            onChange={(event) => update(index, { a: event.target.value })}
            rows={3}
          />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...values, { q: "", a: "" }])}>
        <Plus className="mr-1.5 h-4 w-4" /> Add question
      </Button>
    </div>
  )
}

function SectionsEditor({
  values,
  onChange,
}: {
  values: SeoPageSection[]
  onChange: (values: SeoPageSection[]) => void
}) {
  const update = (index: number, patch: Partial<SeoPageSection>) =>
    onChange(values.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)))
  return (
    <div className="space-y-3">
      {values.map((section, index) => (
        <div key={`section-${index}`} className="space-y-2 rounded border border-border p-3">
          <div className="flex items-center justify-between">
            <Label>Section {index + 1}</Label>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={`Remove section ${index + 1}`}
              onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Input
            aria-label={`Section ${index + 1} heading`}
            value={section.heading}
            placeholder="Heading"
            onChange={(event) => update(index, { heading: event.target.value })}
          />
          <StringListEditor
            label="Paragraphs"
            values={section.body}
            multiline
            onChange={(body) => update(index, { body })}
          />
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onChange([...values, { heading: "", body: [] }])}
      >
        <Plus className="mr-1.5 h-4 w-4" /> Add section
      </Button>
    </div>
  )
}

function ExamplesEditor({
  values,
  onChange,
}: {
  values: SeoPageExample[]
  onChange: (values: SeoPageExample[]) => void
}) {
  const update = (index: number, patch: Partial<SeoPageExample>) =>
    onChange(values.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)))
  return (
    <div className="space-y-3">
      {values.map((example, index) => (
        <div key={`example-${index}`} className="space-y-2 rounded border border-border p-3">
          <div className="flex items-center justify-between">
            <div>
              <Label>Example {index + 1}</Label>
              {example.media?.length ? (
                <p className="text-[11px] text-muted-foreground">
                  {example.media.length} attached media asset(s) preserved
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={`Remove example ${index + 1}`}
              onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Input
            aria-label={`Example ${index + 1} title`}
            value={example.title}
            placeholder="Title"
            onChange={(event) => update(index, { title: event.target.value })}
          />
          <Textarea
            aria-label={`Example ${index + 1} prompt`}
            value={example.prompt ?? ""}
            placeholder="Prompt"
            onChange={(event) => update(index, { prompt: event.target.value })}
            rows={3}
          />
          <Textarea
            aria-label={`Example ${index + 1} result`}
            value={example.result}
            placeholder="Result or explanation"
            onChange={(event) => update(index, { result: event.target.value })}
            rows={3}
          />
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onChange([...values, { title: "", prompt: "", result: "" }])}
      >
        <Plus className="mr-1.5 h-4 w-4" /> Add example
      </Button>
    </div>
  )
}
